(() => {
  'use strict';
  const selector = '[data-bmh-space-video], [data-bmh-auto-video], [data-bmh-video]';
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let cleanup = () => {};
  function initialize() {
    cleanup();
    const videos = [...document.querySelectorAll(selector)];
    if (!videos.length) return;
    const events = new AbortController();
    const { signal } = events;
    const ratios = new Map(videos.map(video => [video, 0]));
    let active = null;
    let desired = null;
    let hovered = null;
    let pending = null;
    let near;
    let visibility;
    const poster = video => {
      if (video.dataset.bmhPoster && !video.poster) video.poster = video.dataset.bmhPoster;
    };
    const hydrate = video => {
      poster(video);
      if (video.getAttribute('src')) return;
      const width = Math.min(960, Math.ceil(video.getBoundingClientRect().width * Math.min(devicePixelRatio || 1, 1.5)));
      const candidates = [...video.querySelectorAll('source[data-bmh-src]')]
        .filter(source => /mp4/i.test(source.type) && video.canPlayType(source.type))
        .sort((a, b) => Number(a.dataset.bmhWidth) - Number(b.dataset.bmhWidth));
      const source = candidates.find(item => Number(item.dataset.bmhWidth) >= width) || candidates.at(-1);
      if (!source) return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.src = source.dataset.bmhSrc;
      video.load();
    };
    const stop = video => {
      video?.pause();
      video?.closest('[data-bmh-video-card]')?.classList.remove('is-playing');
    };
    const play = video => {
      if (signal.aborted || desired !== video || document.hidden || pending === video) return;
      if (active === video && !video.paused) return;
      hydrate(video);
      video.preload = 'auto';
      pending = video;
      video.play().then(() => {
        if (pending === video) pending = null;
        if (signal.aborted || desired !== video || document.hidden) { stop(video); return; }
        videos.forEach(other => { if (other !== video) stop(other); });
        active = video;
        video.closest('[data-bmh-video-card]')?.classList.add('is-playing');
      }).catch(() => { if (pending === video) pending = null; });
    };
    const sync = () => {
      const disabled = document.hidden || motion.matches || navigator.connection?.saveData;
      let next = null;
      let score = -Infinity;
      if (!disabled) ratios.forEach((ratio, video) => {
        if (ratio < .25 || !video.isConnected) return;
        const step = video.closest('[data-bmh-step]');
        if (step && !step.classList.contains('is-active')) return;
        const rect = video.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - innerWidth / 2) / innerWidth;
        const rank = ratio - distance * .3 + (video === active ? .025 : 0) + (video === hovered ? 1 : 0);
        if (rank > score) { next = video; score = rank; }
      });
      desired = next;
      if (!next) { videos.forEach(stop); active = null; return; }
      // Keep the preceding frame until the incoming clip can play; never seek back to 0.
      play(next);
    };
    videos.forEach(video => {
      video.controls = false;
      video.addEventListener('canplay', () => { if (desired === video) play(video); }, { signal });
      const card = video.closest('[data-bmh-video-card]');
      card?.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovered = video; sync(); } }, { signal });
      card?.addEventListener('pointerleave', () => { hovered = null; sync(); }, { signal });
    });
    if ('IntersectionObserver' in window) {
      near = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          poster(entry.target);
          if (!motion.matches && !navigator.connection?.saveData) hydrate(entry.target);
          near.unobserve(entry.target);
        });
      }, { rootMargin: '500px 250px', threshold: 0 });
      visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
        sync();
      }, { threshold: [0, .25, .4, .55, .7, .85, 1] });
      videos.forEach(video => { near.observe(video); visibility.observe(video); });
    } else videos.forEach(poster);
    document.addEventListener('visibilitychange', sync, { signal });
    document.addEventListener('bmh:stepchange', sync, { signal });
    motion.addEventListener('change', sync, { signal });
    cleanup = () => { events.abort(); near?.disconnect(); visibility?.disconnect(); desired = null; videos.forEach(stop); };
  }
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', initialize);
})();
